import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Results } from '@local/client-contracts';
import { MarkdownBlock } from '@local/client-contracts/src/results';
import { KeyName } from '@local/ts-infra';
import { PopupService } from '@local/ui-infra';
import { ContextMenuService } from '@shared/components';
import { HighlightStyle } from '@shared/pipes/highlight.pipe';
import { EventInfo, EventsService, LogService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { KeyboardService } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { RouterService } from '@shared/services/router.service';
import { stopEvent } from '@shared/utils/elements-util';
import { isEqual } from 'lodash';
import { OverlayPanel } from 'primeng/overlaypanel';
import { WorkspacesService } from 'src/app/bar/services';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { CommandBarService } from 'src/app/bar/services/command-bar.service';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { FavoritesService } from 'src/app/bar/services/favorites.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { ResourceViewItemComponent } from '../../../results-views/view-item/resource-view-item.component';
import { onChangeMode } from '../../../results-views/view-item/view-item.component';
import { InvokeCommand } from '../../models/invoke-command.model';
import { FileViewTitle, TelemetryTrigger } from '../../models/results-types';
import { ResultCommandBuilder } from '../../services/result-command-builder.service';
import { buildFileViewItemTitle, isAttachment } from '../../utils/results.util';
import { SearchPopUpService } from '../../../special-views/search-popup/search-popup.service';
import { isExtension } from '@local/common-web';

export interface ShortcutHandler {
  fn: (item: any, trigger: TelemetryTrigger, eventInfo?: Partial<EventInfo>) => any;
  keyBinding?: KeyName[];
}

export interface ShareItem {
  iconUrl: string;
  id: string;
  text: string;
  url: string;
}

type ButtonType = 'favorite' | 'preview' | 'summary' | 'square' | 'feedback' | 'feedbackSent' | 'newIndication';
const BUTTONS_WIDTH: Record<ButtonType, number> = {
  favorite: 14,
  preview: 28,
  summary: 28,
  square: 24,
  feedback: 44,
  feedbackSent: 63,
  newIndication: 16,
};

const BUTTONS_MARGIN_ALL = 16;
const BUTTONS_MARGIN_ITEM = 8;
@Component({
  selector: 'result-item',
  templateUrl: './result-item.component.html',
  styleUrls: ['./result-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultItemComponent extends ResourceViewItemComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly componentName: string = 'ResultsItemComponent';
  protected showResultSectionsDefault: Results.ShowResultSections = {
    showBullets: true,
    showContextMenu: true,
    showTags: true,
    enableCommandBar: true,
    showSnippet: true,
    showSubtitleInSecondLine: false,
    trimSubtitleLength: true,
    showSubtitle: true,
    showInputTitle: false,
    hasResource: true,
    calcBullets: true,
    showSubView: true,
    showAnnotation: true,
    showPreviewIcon: true,
    showSummaryIcon: true,
    showPreviewIconContextMenu: true,
    showLinkIndication: true,
    showFavoriteContextMenuItem: true,
    showAddToCollectionContextMenuItem: true,
    showLinkIndicator: true,
  };
  highlightStyle: HighlightStyle = 'default';
  counterTags = 0;
  downloadAttachmentEnabled: boolean;
  fileItemTitle: FileViewTitle;
  showFullSnippet = false;
  isExtension = isExtension();

  @HostBinding('style.--buttonWidth')
  buttonsWidth: string;

  @HostBinding('style.--square-button-width')
  squareButtonWidth = '24px';

  @HostBinding('attr.isExtensionMode')
  get isExtensionMode() {
    return this.isExtension;
  }

  isShowFeedback: boolean;
  @ViewChild('feedback', { read: ElementRef }) indicateFeedback: ElementRef;
  @ViewChild('op') overlayFeedback: OverlayPanel;

  @Input() hovered: boolean;
  @Input() summaryResourceId: string;
  @ViewChild('resultTitle') resultTitle: ElementRef;
  @Output() titleChanged = new EventEmitter<string>();
  @Output() onFeedbackSent = new EventEmitter<string>();

  private readonly SNIPPET_SECONDARY_BUTTON_HEIGHT = 16;

  get disablePreviewIcon(): boolean {
    return ['people', 'mail', 'cards', 'drafts', 'wiki-collection'].includes(this.pageType);
  }

  get highlights() {
    return this.showResultSections.ignoreHighlights ? [] : this.model.highlights;
  }

  protected get snippetExtraHeight(): number {
    return this.showButtonSnippet ? this.SNIPPET_SECONDARY_BUTTON_HEIGHT : 0;
  }

  constructor(
    hostRef: ElementRef,
    cdr: ChangeDetectorRef,
    logService: LogService,
    applicationsService: ApplicationsService,
    contextMenuService: ContextMenuService,
    resultCommandBuilder: ResultCommandBuilder,
    protected hubService: HubService,
    eventsService: EventsService,
    downloadsService: DownloadsService,
    resourcesService: ResourcesService,
    commandsService: CommandsService,
    commandBarService: CommandBarService,
    popupService: PopupService,
    keyboardService: KeyboardService,
    routerService: RouterService,
    errorHandler: GlobalErrorHandler,
    resultsService: ResultsService,
    protected flagsService: FlagsService,
    protected favoritesService: FavoritesService,
    protected blobsService: BlobsService,
    protected workspacesService: WorkspacesService,
    protected searchPopUpService: SearchPopUpService
  ) {
    super(
      hostRef,
      cdr,
      logService,
      applicationsService,
      contextMenuService,
      resultCommandBuilder,
      hubService,
      eventsService,
      downloadsService,
      resourcesService,
      commandsService,
      commandBarService,
      popupService,
      keyboardService,
      routerService,
      errorHandler,
      resultsService,
      flagsService,
      favoritesService,
      blobsService,
      workspacesService,
      searchPopUpService
    );
  }

  ngOnInit() {
    super.ngOnInit();
    if (this.model?.showResultSections) {
      this.checkShowResultSections();
    }

    this.highlightStyle = this.isEmbed || this.isLauncher || this.hubService.currentLocation === 'home' ? 'bold' : 'default';
    this.flagsService.isEnabled('fileDownloads', this.dragData?.metadata?.resource?.linkId).then((val) => {
      this.downloadAttachmentEnabled = val;
      this.cdr.markForCheck();
    });
  }

  calculateButtonsWidth() {
    if (!this.showResultSections) {
      return;
    }
    if (!this.showResultSections.showContextMenu) {
      this.buttonsWidth = '0px';
      this.squareButtonWidth = '0px';
      this.cdr.markForCheck();
      return;
    }
    let width = BUTTONS_WIDTH.square;
    let countButtons = 1;
    if (this.showResultSections.showFavoriteAction) {
      width += BUTTONS_WIDTH.favorite;
      countButtons++;
    }
    const action = this.model['action'];
    if (action && this.isPreviewClickAction(action) && !this.disablePreviewIcon) {
      width += BUTTONS_WIDTH.preview;
      countButtons++;
    }
    if (action && this.isAvailableSummary(action)) {
      width += BUTTONS_WIDTH.summary;
      countButtons++;
    }
    if (this.showResultSections.showFeedback && (this.showIndicateFeedbackSent || !this.feedbackSent)) {
      const feedBackWidth = this.feedbackSent ? BUTTONS_WIDTH.feedbackSent : BUTTONS_WIDTH.feedback;
      width += feedBackWidth;
      countButtons++;
    }
    if (this.showResultSections.showNewIndication) {
      width += BUTTONS_WIDTH.newIndication;
      countButtons++;
    }
    width += BUTTONS_MARGIN_ALL;
    width += (countButtons - 1) * BUTTONS_MARGIN_ITEM;
    this.buttonsWidth = `${width}px`;
    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.calculateButtonsWidth();
  }

  async onIconError() {
    if (!this.model.link) return;
    const appId = this.model.link.appId;
    const app = appId ? this.applicationsService.apps[appId] : null;
    if (this.model.view) this.model.view.icon = app?.icon || this.fallbackIcon;
  }

  isAttachment(item: any): boolean {
    return isAttachment(item);
  }

  onChanges(val: onChangeMode) {
    super.onChanges(val);
    if (val === 'model') {
      this.calculateButtonsWidth();
    }
    if (val === 'selected' && this.feedbackSent && !this.selected) {
      this.showIndicateFeedbackSent = false;
      this.calculateButtonsWidth();
    }
    const view = this.model?.view;
    if (val !== 'model' || !view) {
      return;
    }
    this.initSnippet();
    this.fileItemTitle = buildFileViewItemTitle(this.model.view.title);
    if (view.subtitle && !view.subtitle.tooltip) {
      this.model.view.subtitle.tooltip = view.subtitle.text;
    }
    if (this.model.showResultSections) {
      this.checkShowResultSections();
    }
    const prevView = this.prevModel?.view;
    if (isEqual(view.bullets, prevView?.bullets) && isEqual(view.tagGroups, prevView?.tagGroups)) {
      return;
    }
    this.manageShortView();
  }

  onUpdateTitle() {
    if (this.model?.['action']?.type === 'files') {
      this.fileItemTitle = buildFileViewItemTitle(this.model.view.title);
    }
  }

  checkShowResultSections() {
    this.setShowResultSections(this.model.showResultSections);
    this.setShowResultSections(this.showResultSectionsDefault, false);
    this.updateVars();
  }

  updateVars() {
    if (!this.showResultSections?.showContextMenu) {
      this.squareButtonWidth = '0px';
    }
  }

  onBlurUInput(event) {
    const { inputElement, defaultInputModel } = event;
    this.resultInputTitle = inputElement;
    super.onBlurUInput(inputElement, defaultInputModel);
    this.cdr.markForCheck();
  }

  private initSnippet() {
    if (!this.model.snippet || this.model.view.snippet?.elements?.length || this.showResultSections?.shortSnippet) return;
    if (this.model.isAdvanced) {
      this.model.view.snippet = {
        elements: [
          {
            type: 'markdown_block_snippet',
            content: this.model.snippet,
          } as MarkdownBlock,
        ],
      };
      return;
    }

    this.model.view.snippet = {
      elements: [
        {
          type: 'rich_text',
          elements: [
            {
              type: 'rich_text_section',
              elements: [{ type: 'text', text: this.model.snippet } as Results.RichTextText],
            } as Results.RichTextSection,
          ],
        } as Results.RichTextBlock,
      ],
    } as Results.Snippet;
  }

  focusOnInput(event) {
    this.resultInputTitle = event;
    super.displayInput();
    this.cdr.markForCheck();
  }

  onFeedbackSentFunc() {
    this.onFeedbackSent.emit(this.model.id);
    this.feedbackSent = true;
    this.showIndicateFeedbackSent = true;
    this.calculateButtonsWidth();
    this.cdr.markForCheck();
  }

  onShowFeedback(event) {
    stopEvent(event);
    this.invokeSelectedIndex.emit();
    this.isShowFeedback = true;
    this.overlayFeedback.show(event);
  }

  onHideFeedback(event) {
    if (event?.target?.closest('.feedback-container') || this.feedbackDisplayView === 'wrongReasonFree' || !this.isShowFeedback) {
      return;
    }
    this.isShowFeedback = false;
    this.overlayFeedback.hide();
  }

  onMouseClick(invokeData: InvokeCommand) {
    this.onInvoke({ ...invokeData, trigger: 'mouse_click' });
  }

  galleryInvoke($event: { event: MouseEvent; type: string }) {
    switch ($event.type) {
      case 'contextmenu':
        this.openContextMenu($event.event, 'actions');
        break;
      case 'summary':
        this.onSummary($event.event, this.summaryResourceId);
        break;
      case 'preview':
        this.onPreviewClick();
        if (this.isClickable) {
          $event.event.stopPropagation();
        }
        break;
      case 'title':
        this.onInvoke({ command: this.model?.view?.title?.onClick, source: 'title' });
        break;
      case 'checkbox':
        this.changeSelection.emit($event.event);
        break;
      case 'onDisplayInput':
        this.focusOnInput($event.event);
        break;
      case 'onBlurUInput':
        this.onBlurUInput($event.event);
        break;
      case 'onClickOut':
        this.onEditTitleClickOut();
        break;
    }
  }

  onChangeSnippetMode($event) {
    this.showFullSnippet = !this.showFullSnippet;
    this.maxHeightSnippet();
    $event.stopPropagation();
  }
}
